import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "@/lib/brand";
import Landing from "@/pages/Landing";
import LandingAr from "@/pages/LandingAr";

// ─────────────────────────────────────────────────────────────────
// Prototype — Photographic Destinations (resting state only)
//
// Each navigation destination is its own image-based navigation
// element. Photographs belong to destinations individually — not to
// the header as a shared background.
//
// Destinations without authentic imagery render as restrained black
// typographic navigation elements (no stock, no reuse, no fabrication).
// This intentional asymmetry honestly represents the available archive.
//
// Aesthetic details (hover behaviour, dimensions, cropping, animation)
// deliberately deferred until the user evaluates the structural
// resting state.
//
// Preservation: all live routes and every earlier prototype untouched.
// Landing.jsx and LandingAr.jsx not modified.
// ─────────────────────────────────────────────────────────────────

const DESTINATIONS = [
    {
        id: "home",
        to: "/",
        end: true,
        labelEn: "HOME",
        labelAr: "الرئيسية",
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/5n6pde8o_FB21FACC-7058-44F0-AC7A-FA1B1432698C.JPG",
        objectPos: "50% 42%",
    },
    {
        id: "about",
        to: "/about",
        labelEn: "ABOUT",
        labelAr: "عن المؤسسة",
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/u65gd357_IMG-20260503-WA0051.jpg",
        objectPos: "50% 40%",
    },
    {
        id: "gathering",
        to: "/gathering",
        labelEn: "THE GATHERING",
        labelAr: "The Gathering",
        protectedEn: true,
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/tf8y44s5_F3209F41-0B30-4C13-8807-7FCBDFB18ABD.PNG",
        objectPos: "50% 42%",
    },
    {
        id: "events",
        to: "/events",
        labelEn: "EVENTS",
        labelAr: "الفعاليات",
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/l3h9h8v0_35B1CA2D-6A44-433F-A0BB-19EE971F256E.JPEG",
        objectPos: "50% 48%",
    },
    {
        id: "gallery",
        to: "/gallery",
        labelEn: "GALLERY",
        labelAr: "المعرض",
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/eyed6v8w_E4BE464A-17CA-400C-9126-D68CE352C4E2.JPG",
        objectPos: "50% 50%",
    },
    {
        id: "sponsors",
        to: "/sponsors",
        labelEn: "SPONSORS",
        labelAr: "الرعاة",
        image: null, // No authentic archive image — remains typographic
    },
    {
        id: "store",
        to: "/store",
        labelEn: "STORE",
        labelAr: "المتجر",
        image: null,
    },
    {
        id: "contact",
        to: "/contact",
        labelEn: "CONTACT",
        labelAr: "تواصل",
        image: null,
    },
];

function renderLabel(item, isAr) {
    if (item.labelEn === "THE GATHERING") {
        return (
            <span dir="ltr" className="inline-block">
                THE <span className="text-[var(--crewz-gold)]">GATHERING</span>
            </span>
        );
    }
    if (item.protectedEn && isAr) {
        return (
            <span dir="ltr" className="inline-block">
                {item.labelEn}
            </span>
        );
    }
    return isAr ? item.labelAr : item.labelEn;
}

// A photographic destination — full-height segment whose body is
// the destination's own authentic photograph. Label overlaid at the
// lower baseline. Deliberately minimal at rest so the visual
// structure is evaluated before aesthetic refinements.
function PhotographicDestination({ item, isAr }) {
    const useArabicFont = isAr && !item.protectedEn && item.labelEn !== "THE GATHERING";
    return (
        <NavLink
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
                `photo-destination relative flex-1 h-full block overflow-hidden ${isActive ? "is-active" : ""}`
            }
            data-testid={`photo-dest-${item.id}`}
        >
            <img
                src={item.image}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover select-none"
                style={{ objectPosition: item.objectPos }}
                draggable={false}
            />
            {/* Restrained lower gradient for label legibility only */}
            <div
                className="absolute inset-x-0 bottom-0 h-14 pointer-events-none"
                style={{
                    background:
                        "linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.35) 55%, transparent 100%)",
                }}
                aria-hidden="true"
            />
            <span
                className={`absolute inset-x-0 bottom-3 px-3 flex items-baseline leading-none whitespace-nowrap ${
                    isAr ? "justify-end" : "justify-start"
                } ${
                    useArabicFont
                        ? "font-arabic text-[12px] font-bold text-white/95"
                        : "font-body uppercase tracking-[0.16em] text-[10px] font-bold text-white/95"
                }`}
                style={{ textShadow: "0 1px 2px rgba(0,0,0,0.6)" }}
            >
                {renderLabel(item, isAr)}
            </span>
        </NavLink>
    );
}

// A typographic destination — the destinations that currently lack
// authentic imagery remain restrained black text elements. No stock
// photography, no reused photographs, no fabricated substitutes.
// The visible asymmetry is deliberate.
function TypographicDestination({ item, isAr }) {
    const useArabicFont = isAr && !item.protectedEn && item.labelEn !== "THE GATHERING";
    return (
        <NavLink
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
                `typographic-destination relative flex-1 h-full flex items-center justify-center bg-[#0f0f0f] ${isActive ? "is-active" : ""}`
            }
            data-testid={`text-dest-${item.id}`}
        >
            <span
                className={`leading-none whitespace-nowrap ${
                    useArabicFont
                        ? "font-arabic text-[13px] font-bold text-zinc-300"
                        : "font-body uppercase tracking-[0.2em] text-[11px] font-bold text-zinc-300"
                }`}
            >
                {renderLabel(item, isAr)}
            </span>
        </NavLink>
    );
}

function PhotographicHeader({ isAr, toggleTo }) {
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            className="photo-header sticky top-0 z-50 bg-[#0f0f0f] border-b border-white/5"
            data-testid="site-navbar-photo-destinations"
            dir={isAr ? "rtl" : "ltr"}
        >
            <div className="flex items-stretch" style={{ height: "104px" }}>
                {/* Language toggle zone */}
                <div className="hidden lg:flex items-center px-6" style={{ width: "128px" }}>
                    <Link
                        to={toggleTo}
                        className={
                            isAr
                                ? "font-body uppercase tracking-[0.2em] text-[12px] font-bold text-zinc-200 hover:text-white transition-colors duration-300"
                                : "font-arabic normal-case tracking-normal text-[14px] font-bold text-zinc-200 hover:text-white transition-colors duration-300"
                        }
                        dir={isAr ? "ltr" : "rtl"}
                        data-testid="photo-lang-switcher"
                    >
                        {isAr ? "English" : "العربية"}
                    </Link>
                </div>

                {/* Destinations row — each is its own navigation object.
                    Photographic and typographic destinations share the
                    same row without cards, without gaps. */}
                <nav
                    className="hidden lg:flex flex-1 h-full"
                    aria-label="Primary navigation"
                    data-testid="photo-destinations-row"
                >
                    {DESTINATIONS.map((item) =>
                        item.image ? (
                            <PhotographicDestination key={item.id} item={item} isAr={isAr} />
                        ) : (
                            <TypographicDestination key={item.id} item={item} isAr={isAr} />
                        )
                    )}
                </nav>

                {/* Wordmark zone */}
                <div className="hidden lg:flex items-center px-6" style={{ width: "168px", justifyContent: isAr ? "flex-start" : "flex-end" }}>
                    <Link
                        to={isAr ? "/prototypes/photographic-destinations?lang=ar" : "/prototypes/photographic-destinations"}
                        className="flex items-center"
                        data-testid="photo-logo"
                    >
                        <img
                            src={LOGO_URL}
                            alt="CrewZNatioN"
                            className="h-14 md:h-16 w-auto select-none"
                            draggable={false}
                        />
                    </Link>
                </div>

                {/* Mobile — text drawer, unchanged pattern */}
                <div className="lg:hidden flex items-center justify-between w-full px-6">
                    <Link
                        to={isAr ? "/prototypes/photographic-destinations?lang=ar" : "/prototypes/photographic-destinations"}
                        className="flex items-center"
                        data-testid="photo-logo-mobile"
                    >
                        <img src={LOGO_URL} alt="CrewZNatioN" className="h-12 w-auto select-none" draggable={false} />
                    </Link>
                    <button
                        className="text-white p-2"
                        onClick={() => setOpen((v) => !v)}
                        data-testid="photo-mobile-toggle"
                        aria-label={isAr ? "فتح القائمة" : "Toggle menu"}
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden border-t border-white/5 bg-[#111111]/95 backdrop-blur-xl">
                    <div className={`px-6 py-6 flex flex-col gap-1 ${isAr ? "font-arabic" : "font-body"}`}>
                        {DESTINATIONS.map((item) => (
                            <NavLink
                                key={item.id}
                                to={item.to}
                                end={item.end}
                                className={({ isActive }) =>
                                    `px-2 py-3 text-xs tracking-[0.22em] uppercase border-b border-white/5 ${
                                        isActive ? "text-[var(--crewz-gold)]" : "text-zinc-300"
                                    }`
                                }
                            >
                                {renderLabel(item, isAr)}
                            </NavLink>
                        ))}
                    </div>
                </div>
            )}
        </header>
    );
}

export default function PhotographicDestinationsPrototype() {
    const location = useLocation();
    const isAr = location.search.includes("lang=ar");
    const toggleTo = isAr
        ? "/prototypes/photographic-destinations"
        : "/prototypes/photographic-destinations?lang=ar";

    return (
        <div
            className="photographic-destinations-prototype min-h-screen bg-[var(--crewz-bg)] text-[var(--crewz-text)]"
            data-testid="photographic-destinations-prototype"
            dir={isAr ? "rtl" : "ltr"}
        >
            <PhotographicHeader isAr={isAr} toggleTo={toggleTo} />

            <div className="photographic-destinations-body">
                {isAr ? <LandingAr /> : <Landing />}
            </div>

            <style>{`
                .photographic-destinations-prototype .photographic-destinations-body [data-testid="site-navbar"],
                .photographic-destinations-prototype .photographic-destinations-body [data-testid="site-navbar-ar"] {
                    display: none !important;
                }
            `}</style>
        </div>
    );
}
